import type { ReactNode } from "react";
import { useEffect, useRef, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import {
  useMe, useLogout, useTheme, SEKTION_EREIGNIS, sektionId,
  IconBuch, IconDiagramm, IconEuro, IconFile, IconGear, IconListe, IconMore, IconParagraf, IconUsers,
} from "@vimaana/ui";
import { useAufgabenAnzahl } from "../hooks/useAufgaben";

const APP_NAME = "pflege"; // eigener Auth-Scope (siehe App.tsx)

type Ziel = { to: string; label: string; icon: () => JSX.Element; zahl?: "aufgaben" };

/**
 * Die Leiste wie in Kontor: Gruppen, gleiche Symbole für gleiche Dinge
 * (Kunden = IconUsers, Geld = IconEuro, Einstellungen = IconGear).
 * Konzept: gehirn/…/pflege-app/dokumente/Konzept - Büro-Seite neu ordnen.md
 */
const NAV: { group: string; items: Ziel[] }[] = [
  { group: "Betrieb", items: [
    { to: "/aufgaben", label: "Aufgaben", icon: IconListe, zahl: "aufgaben" },
    { to: "/kunden", label: "Kunden", icon: IconUsers },
    { to: "/vertraege", label: "Verträge", icon: IconFile },
  ] },
  { group: "Geld", items: [
    { to: "/zahlungen", label: "Zahlungen", icon: IconEuro },
    { to: "/auswertung", label: "Auswertung", icon: IconDiagramm },
  ] },
  { group: "System", items: [
    { to: "/katalog", label: "Leistungskatalog", icon: IconBuch },
    { to: "/vertragstext", label: "Vertragstext und AGB", icon: IconParagraf },
    { to: "/einstellungen", label: "Einstellungen", icon: IconGear },
  ] },
];

/** Telefon: vier Alltagsziele, der Rest einen Tipp entfernt hinter „Mehr" — wie Kontor. */
const TABBAR: Ziel[] = [
  { to: "/aufgaben", label: "Aufgaben", icon: IconListe },
  { to: "/kunden", label: "Kunden", icon: IconUsers },
  { to: "/vertraege", label: "Verträge", icon: IconFile },
  { to: "/zahlungen", label: "Zahlungen", icon: IconEuro },
];
const TABBAR_REST: Ziel[] = [
  { to: "/auswertung", label: "Auswertung", icon: IconDiagramm },
  { to: "/katalog", label: "Leistungskatalog", icon: IconBuch },
  { to: "/vertragstext", label: "Vertragstext und AGB", icon: IconParagraf },
  { to: "/einstellungen", label: "Einstellungen", icon: IconGear },
];

/** Seiten, die zu einem Ziel gehören, ohne selbst in der Leiste zu stehen. */
const ZUGEHOERIG: Record<string, string[]> = {
  "/aufgaben": ["/fristen"],
  "/vertragstext": ["/bedingungen"],
};

/**
 * App-Rahmen — derselbe wie Kontor (`@vimaana/ui/shell`):
 *   ≥1024 px volle Seitenleiste · 761–1023 px Symbolschiene · ≤760 px Tableiste
 *   mit „Mehr"-Blatt. Der Seitenkopf steht im Inhalt, wie auf Kontors Seiten.
 */
export default function Shell({
  title, subtitle, action, breit, ohneKopf, children,
}: {
  title: string;
  subtitle?: string;
  action?: ReactNode;
  /** Deckel aufheben — für Seiten mit zwei Spalten nebeneinander (Vertragstext). */
  breit?: boolean;
  /** Die Seite bringt ihren eigenen Kopf mit (Vertragsseite: Kopf einer Akte wie in Kontor). */
  ohneKopf?: boolean;
  children: ReactNode;
}) {
  const qc = useQueryClient();
  const loc = useLocation();
  const { theme, toggleTheme } = useTheme();
  const { data: me } = useMe(APP_NAME);
  const logout = useLogout(APP_NAME);
  const aufgaben = useAufgabenAnzahl();
  const [mehrOffen, setMehrOffen] = useState(false);
  const mehrButtonRef = useRef<HTMLButtonElement>(null);
  const sheetRef = useRef<HTMLDivElement>(null);
  const name = me?.full_name || me?.email || "Angemeldet";

  useEffect(() => { setMehrOffen(false); }, [loc.pathname]);
  // Sprungziel aus der Adresse (#preisrunde, #einsaetze): erst nach dem Laden
  // der Daten steht das Ziel im Dokument, deshalb ein kurzer Nachlauf.
  useEffect(() => {
    if (!loc.hash) return;
    const id = decodeURIComponent(loc.hash.slice(1));
    const t = window.setTimeout(() => {
      window.dispatchEvent(new CustomEvent(SEKTION_EREIGNIS, { detail: id }));
      (document.getElementById(id) || document.getElementById(sektionId(id)))
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 350);
    return () => window.clearTimeout(t);
  }, [loc.pathname, loc.hash]);
  useEffect(() => {
    if (!mehrOffen) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") { setMehrOffen(false); mehrButtonRef.current?.focus(); }
    }
    document.addEventListener("keydown", onKey);
    sheetRef.current?.querySelector<HTMLElement>("a, button")?.focus();
    return () => document.removeEventListener("keydown", onKey);
  }, [mehrOffen]);

  const aktiv = (to: string) =>
    loc.pathname.startsWith(to) || (ZUGEHOERIG[to] || []).some(p => loc.pathname.startsWith(p));
  const restAktiv = TABBAR_REST.some(it => aktiv(it.to));

  return (
    <div className="kontor-layout">
      <a className="kontor-skip-link" href="#pf-inhalt">Zum Inhalt springen</a>

      <aside className="kontor-sidebar">
        <div className="kontor-brand">
          <div className="kontor-logo">VG</div>
          <div className="kontor-brand-text">
            <div className="kontor-brand-name">Vimaana Garten</div>
            <div className="kontor-brand-subtitle">Pflege</div>
          </div>
        </div>
        <nav className="kontor-nav" aria-label="Hauptnavigation">
          {NAV.map(g => (
            <div className="kontor-nav-group" key={g.group}>
              <div className="kontor-group-label">{g.group}</div>
              {g.items.map(it => (
                <NavLink key={it.to} to={it.to} title={it.label}
                  className={() => `kontor-nav-item${aktiv(it.to) ? " active" : ""}`}>
                  <it.icon /><span>{it.label}</span>
                  {it.zahl === "aufgaben" && aufgaben > 0 && <em className="kontor-nav-zahl">{aufgaben}</em>}
                </NavLink>
              ))}
            </div>
          ))}
        </nav>
        <div className="kontor-sidebar-footer">
          <button className="kontor-theme-toggle" type="button" onClick={toggleTheme}
            aria-label={theme === "dark" ? "Auf helles Design wechseln" : "Auf dunkles Design wechseln"}
            title={theme === "dark" ? "Helles Design" : "Dunkles Design"}>
            <span aria-hidden="true">{theme === "dark" ? "☀" : "☾"}</span>
            <span className="kontor-footer-label">{theme === "dark" ? "Hell" : "Dunkel"}</span>
          </button>
          <button className="kontor-reload" type="button" onClick={() => qc.invalidateQueries()} title="Daten neu laden">
            <span aria-hidden="true">↻</span>
            <span className="kontor-footer-label"> Daten neu laden</span>
          </button>
          <div className="kontor-footer-row">
            <span className="kontor-profile-link" title={name}>{name}</span>
            <button type="button" className="pf-abmelden" onClick={() => logout.mutate()}>Abmelden</button>
          </div>
        </div>
      </aside>

      <main className="kontor-main" id="pf-inhalt">
        <div className={"pf-seite" + (breit ? " is-breit" : "")}>
          {!ohneKopf && <header className="pf-kopf">
            <div className="pf-kopf-titel">
              <h1>{title}</h1>
              {subtitle ? <div className="pf-kopf-unter">{subtitle}</div> : null}
            </div>
            {action ? <div className="pf-kopf-aktion">{action}</div> : null}
          </header>}
          {children}
        </div>
      </main>

      <nav className="kontor-tabbar" aria-label="Hauptnavigation (mobil)">
        {TABBAR.map(it => (
          <NavLink key={it.to} to={it.to} className={() => `kontor-tab${aktiv(it.to) ? " active" : ""}`}>
            <it.icon /><span>{it.label}</span>
          </NavLink>
        ))}
        <button ref={mehrButtonRef} type="button"
          className={`kontor-tab${restAktiv || mehrOffen ? " active" : ""}`}
          aria-expanded={mehrOffen} aria-haspopup="dialog" onClick={() => setMehrOffen(v => !v)}>
          <IconMore /><span>Mehr</span>
        </button>
      </nav>

      {mehrOffen && (
        <div className="kontor-sheet-backdrop" onClick={() => setMehrOffen(false)}>
          <div className="kontor-sheet" role="dialog" aria-modal="true" aria-label="Weitere Bereiche"
            ref={sheetRef} onClick={e => e.stopPropagation()}>
            <div className="kontor-sheet-griff" aria-hidden="true" />
            {TABBAR_REST.map(it => (
              <NavLink key={it.to} to={it.to} className={() => `kontor-sheet-item${aktiv(it.to) ? " active" : ""}`}>
                <it.icon /><span>{it.label}</span>
              </NavLink>
            ))}
            <div className="kontor-sheet-actions">
              <button type="button" onClick={toggleTheme}>
                <span aria-hidden="true">{theme === "dark" ? "☀" : "☾"}</span> {theme === "dark" ? "Helles Design" : "Dunkles Design"}
              </button>
              <button type="button" onClick={() => { qc.invalidateQueries(); setMehrOffen(false); }}>
                <span aria-hidden="true">↻</span> Daten neu laden
              </button>
              <button type="button" onClick={() => logout.mutate()}>Abmelden</button>
            </div>
            <button type="button" className="kontor-sheet-close" onClick={() => setMehrOffen(false)}>Schließen</button>
          </div>
        </div>
      )}
    </div>
  );
}
